import React, { useState, useRef, useEffect } from 'react';
import EmojiPicker from 'emoji-picker-react';
import AIPopover from './AIPopover';
import {
  FiPlus, FiBold, FiItalic, FiUnderline, FiLink, FiCode,
  FiType, FiList, FiAlignLeft, FiImage, FiSmile, FiAtSign,
  FiMinus, FiX, FiZap
} from 'react-icons/fi';

const RichTextEditor = ({ value, onChange, onBlur, placeholder }) => {
  const [showInsertMenu, setShowInsertMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAIPopover, setShowAIPopover] = useState(false);
  const [aiPopoverProps, setAIPopoverProps] = useState(null);
  const [insertMenuPos, setInsertMenuPos] = useState(null);
  const editorRef = useRef(null);
  const insertButtonRef = useRef(null);
  const emojiButtonRef = useRef(null);

  // Sincronizar contenido cuando cambia desde fuera
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      // Evitar sobrescribir si el usuario está escribiendo y el cambio es mínimo
      // Pero aquí asumimos control total si value cambia drásticamente
      if (document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = value || '';
      } else if (!value) {
        editorRef.current.innerHTML = '';
      }
    }
  }, [value]);

  // Cerrar menús al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        insertButtonRef.current &&
        !insertButtonRef.current.contains(event.target) &&
        !event.target.closest('.insert-menu')
      ) {
        setShowInsertMenu(false);
      }
      if (
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(event.target) &&
        !event.target.closest('.emoji-picker-container')
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleInsertMenu = () => {
    if (!showInsertMenu && insertButtonRef.current) {
      const rect = insertButtonRef.current.getBoundingClientRect();
      setInsertMenuPos({
        top: rect.bottom + 5,
        left: rect.left
      });
    }
    setShowInsertMenu(!showInsertMenu);
    setShowEmojiPicker(false);
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
    setShowInsertMenu(false);
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleBlur = () => {
    if (editorRef.current && onBlur) {
      onBlur();
    }
  };

  const applyFormat = (e, command, value = null) => {
    e.preventDefault(); // Previene perder el foco del editor
    document.execCommand(command, false, value);
    handleInput();
  };

  const onEmojiClick = (emojiObject) => {
    const emoji = emojiObject.emoji;
    const selection = window.getSelection();

    // Asegurarse de que el foco esté en el editor antes de insertar
    if (!editorRef.current.contains(selection.anchorNode)) {
      editorRef.current.focus();
    }

    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const emojiNode = document.createTextNode(emoji);
      range.insertNode(emojiNode);

      // Mover el cursor después del emoji
      range.setStartAfter(emojiNode);
      range.setEndAfter(emojiNode);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      // Fallback si no hay selección
      editorRef.current.innerHTML += emoji;
    }

    setShowEmojiPicker(false);
    handleInput();
  };

  const insertElement = (element) => {
    // Restaurar foco antes de ejecutar comandos
    editorRef.current.focus();

    const selection = window.getSelection();
    let range;

    if (selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
    } else {
      range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
    }

    let content;
    switch (element) {
      case 'heading1':
        document.execCommand('formatBlock', false, 'H1');
        break;

      case 'heading2':
        document.execCommand('formatBlock', false, 'H2');
        break;

      case 'bullet-list':
        document.execCommand('insertUnorderedList');
        break;

      case 'numbered-list':
        document.execCommand('insertOrderedList');
        break;

      case 'quote':
        document.execCommand('formatBlock', false, 'BLOCKQUOTE');
        break;

      case 'divider':
        document.execCommand('insertHorizontalRule');
        break;

      case 'code-block':
        // Code block simple implementation
        content = document.createElement('pre');
        content.style.backgroundColor = '#f3f4f6';
        content.style.padding = '10px';
        content.style.borderRadius = '5px';
        content.innerHTML = '<code>Code...</code>';
        // This is tricky with execCommand, inserting HTML directly
        document.execCommand('insertHTML', false, content.outerHTML);
        break;

      case 'paragraph':
        document.execCommand('formatBlock', false, 'P');
        break;

      default:
        return;
    }

    setShowInsertMenu(false);
    handleInput();
  };

  const createLink = (e) => {
    e.preventDefault();
    const url = prompt('Ingresa la URL:');
    if (url) {
      document.execCommand('createLink', false, url);
      handleInput();
    }
  };

  const insertMenuItems = [
    { id: 'paragraph', icon: FiAlignLeft, label: 'Párrafo', description: 'Texto simple' },
    { id: 'heading1', icon: FiType, label: 'Título 1', description: 'Encabezado grande' },
    { id: 'heading2', icon: FiType, label: 'Título 2', description: 'Encabezado mediano' },
    { id: 'bullet-list', icon: FiList, label: 'Lista con viñetas', description: 'Lista de elementos' },
    { id: 'numbered-list', icon: FiList, label: 'Lista numerada', description: 'Lista ordenada' },
    { id: 'quote', icon: FiAlignLeft, label: 'Cita', description: 'Texto citado' },
    { id: 'divider', icon: FiMinus, label: 'Salto de sección', description: 'Línea divisoria' },
    // { id: 'code-block', icon: FiCode, label: 'Bloque de código', description: 'Código formateado' },
  ];

  return (
    <div className="relative group">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border border-gray-200 rounded-t-lg bg-gray-50 opacity-100 transition-opacity">
        {/* Botón + para insertar elementos */}
        <button
          ref={insertButtonRef}
          onMouseDown={(e) => e.preventDefault()}
          onClick={toggleInsertMenu}
          className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${showInsertMenu ? 'bg-gray-200 text-blue-600' : 'text-gray-600'
            }`}
          title="Insertar elemento"
          type="button"
        >
          <FiPlus size={16} />
        </button>

        <div className="w-px h-5 bg-gray-300 mx-1" />

        {/* Botones de formato */}
        <button
          onMouseDown={(e) => applyFormat(e, 'bold')}
          className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors"
          title="Negrita (Ctrl+B)"
          type="button"
        >
          <FiBold size={16} />
        </button>

        <button
          onMouseDown={(e) => applyFormat(e, 'italic')}
          className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors"
          title="Cursiva (Ctrl+I)"
          type="button"
        >
          <FiItalic size={16} />
        </button>

        <button
          onMouseDown={(e) => applyFormat(e, 'underline')}
          className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors"
          title="Subrayado (Ctrl+U)"
          type="button"
        >
          <FiUnderline size={16} />
        </button>

        <button
          onMouseDown={createLink}
          className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors"
          title="Enlace"
          type="button"
        >
          <FiLink size={16} />
        </button>

        <div className="w-px h-5 bg-gray-300 mx-1" />

        {/* Botón IA */}
        <button
          onClick={(e) => {
            // e.preventDefault(); // Click normal para asegurar que la UI responda
            if (!editorRef.current) return;

            const content = editorRef.current.innerText;
            // Permitir abrir incluso si está vacío para sugerir texto? No, mejor solo si hay algo o placeholder.
            // Pero el usuario dijo "holaaa" y no abrió.
            // Quitamos el check estricto de trim por si acaso hay espacios invisibles, o dejamos que el Popover maneje "generar desde cero"?
            // Por seguridad dejaremos que pase si tiene length > 0

            const rect = e.currentTarget.getBoundingClientRect();

            if (setShowAIPopover) {
              setAIPopoverProps({
                initialContent: content,
                position: { top: rect.bottom + 5, left: rect.left },
                onInsert: (newContent) => {
                  editorRef.current.innerHTML = newContent;
                  handleInput();
                }
              });
              setShowAIPopover(true);
            }
          }}
          className="p-1.5 rounded bg-indigo-50 text-indigo-600 hover:bg-indigo-100 hover:text-indigo-700 transition-colors flex items-center gap-1"
          title="Mejorar redacción con IA (Reescribir)"
          type="button"
        >
          <FiZap size={14} />
          <span className="text-[10px] font-bold">IA</span>
        </button>

        <div className="w-px h-5 bg-gray-300 mx-1" />

        <button
          onMouseDown={(e) => applyFormat(e, 'insertUnorderedList')}
          className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors"
          title="Lista"
          type="button"
        >
          <FiList size={16} />
        </button>

        <div className="relative">
          <button
            ref={emojiButtonRef}
            onMouseDown={(e) => e.preventDefault()}
            onClick={toggleEmojiPicker}
            className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${showEmojiPicker ? 'bg-gray-200 text-blue-600' : 'text-gray-600'
              }`}
            title="Emoji"
            type="button"
          >
            <FiSmile size={16} />
          </button>

          {/* Selector de Emojis */}
          {showEmojiPicker && (
            <div className="emoji-picker-container absolute top-full left-0 mt-2 z-50 shadow-2xl rounded-lg">
              {/* Botón cerrar para móviles o conveniencia */}
              <div className="bg-white p-1 flex justify-end border-b border-gray-100 rounded-t-lg">
                <button onClick={() => setShowEmojiPicker(false)} className="p-1 hover:bg-gray-100 rounded-full"><FiX /></button>
              </div>
              <EmojiPicker
                onEmojiClick={onEmojiClick}
                width={300}
                height={400}
                previewConfig={{ showPreview: false }}
                skinTonesDisabled
                searchDisabled={false}
              />
            </div>
          )}
        </div>

      </div>

      {/* Editor ContentEditable */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onBlur={handleBlur}
        className="w-full min-h-[120px] p-4 border border-gray-200 border-t-0 rounded-b-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm text-gray-700 bg-white overflow-y-auto rich-text-content"
        style={{ maxHeight: '400px', lineHeight: '1.6' }}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />

      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contenteditable] a {
          color: #3b82f6;
          text-decoration: underline;
        }
        [contenteditable]:focus {
          outline: none;
        }
        /* Estilos básicos para contenido */
        .rich-text-content h1 { font-size: 1.5rem; font-weight: bold; margin-bottom: 0.5rem; }
        .rich-text-content h2 { font-size: 1.25rem; font-weight: bold; margin-bottom: 0.5rem; }
        .rich-text-content ul { list-style-type: disc; padding-left: 1.5rem; margin-bottom: 0.5rem; }
        .rich-text-content ol { list-style-type: decimal; padding-left: 1.5rem; margin-bottom: 0.5rem; }
        .rich-text-content blockquote { border-left: 4px solid #e5e7eb; padding-left: 1rem; color: #6b7280; font-style: italic; margin-bottom: 0.5rem; }
      `}</style>

      {/* Menú de Inserción */}
      {showInsertMenu && insertMenuPos && (
        <div
          className="insert-menu fixed bg-white border border-gray-200 rounded-lg shadow-xl z-50 w-72 max-h-96 overflow-y-auto"
          style={{
            top: insertMenuPos.top,
            left: insertMenuPos.left
          }}
        >
          <div className="p-2 text-xs text-gray-500 font-semibold border-b border-gray-100">
            Insertar
          </div>
          <div className="py-1">
            {insertMenuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertElement(item.id);
                  }}
                  className="w-full flex items-start gap-3 px-3 py-2 hover:bg-gray-50 transition-colors text-left"
                  type="button"
                >
                  <Icon size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900">{item.label}</div>
                    <div className="text-xs text-gray-500">{item.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* AI Popover */}
      {showAIPopover && aiPopoverProps && (
        <AIPopover
          initialContent={aiPopoverProps.initialContent}
          onInsert={(content) => {
            aiPopoverProps.onInsert(content);
            setShowAIPopover(false);
          }}
          onClose={() => setShowAIPopover(false)}
          position={aiPopoverProps.position}
          targetName="descripción"
        />
      )}
    </div>
  );
};

export default RichTextEditor;
